import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateShort = (d) => {
  if (!d) return 'N/A';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Credential Audit Trail ───────────────────────────────────────────────────
const CREDENTIAL_EVENTS = [
  { type: 'license_verified', label: 'Licence vérifiée', icon: 'BadgeCheck', color: 'text-green-600 bg-green-100' },
  { type: 'license_submitted', label: 'Licence soumise', icon: 'FileText', color: 'text-blue-600 bg-blue-100' },
  { type: 'license_expired', label: 'Licence expirée', icon: 'AlertTriangle', color: 'text-red-600 bg-red-100' },
  { type: 'profile_updated', label: 'Profil mis à jour', icon: 'Edit', color: 'text-amber-600 bg-amber-100' },
  { type: 'verification_requested', label: 'Vérification demandée', icon: 'Search', color: 'text-purple-600 bg-purple-100' },
  { type: 'status_changed', label: 'Statut modifié', icon: 'RefreshCw', color: 'text-orange-600 bg-orange-100' },
  { type: 'document_uploaded', label: 'Document téléversé', icon: 'Upload', color: 'text-teal-600 bg-teal-100' },
  { type: 'background_check_initiated', label: 'Vérification antécédents lancée', icon: 'UserCheck', color: 'text-indigo-600 bg-indigo-100' },
  { type: 'background_check_passed', label: 'Antécédents validés', icon: 'ShieldCheck', color: 'text-green-600 bg-green-100' },
  { type: 'background_check_failed', label: 'Antécédents rejetés', icon: 'ShieldX', color: 'text-red-600 bg-red-100' },
];

const getEventConfig = (type) =>
  CREDENTIAL_EVENTS?.find(e => e?.type === type) || { label: type, icon: 'Circle', color: 'text-gray-600 bg-gray-100' };

const CredentialAuditTrail = ({ notary }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!notary?.id) return;
    loadAuditLogs();
  }, [notary?.id]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('notary_credential_audit_logs')
        ?.select('*')
        ?.eq('notary_id', notary?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (data?.length > 0) {
        setAuditLogs(data);
      } else {
        // Generate synthetic audit trail from notary data
        const synthetic = [];
        if (notary?.created_at) {
          synthetic?.push({ id: 'syn-1', event_type: 'license_submitted', description: `Licence ${notary?.license_number || 'N/A'} soumise pour vérification`, performed_by: 'Système', created_at: notary?.created_at, metadata: {} });
        }
        if (notary?.is_verified) {
          synthetic?.push({ id: 'syn-2', event_type: 'license_verified', description: `Licence professionnelle vérifiée et approuvée`, performed_by: 'Admin', created_at: notary?.updated_at || notary?.created_at, metadata: {} });
        }
        if (notary?.updated_at && notary?.updated_at !== notary?.created_at) {
          synthetic?.push({ id: 'syn-3', event_type: 'profile_updated', description: 'Informations du profil mises à jour', performed_by: 'Admin', created_at: notary?.updated_at, metadata: {} });
        }
        setAuditLogs(synthetic);
      }
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setAuditLogs([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-brand-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-foreground">Journal d'Audit des Credentials</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{auditLogs?.length} événement{auditLogs?.length !== 1 ? 's' : ''} enregistré{auditLogs?.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={loadAuditLogs}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/40 hover:bg-muted/60 rounded-lg transition-colors border border-border"
        >
          <Icon name="RefreshCw" size={12} />
          Actualiser
        </button>
      </div>

      {auditLogs?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-12 h-12 rounded-xl bg-muted/40 flex items-center justify-center">
            <Icon name="ClipboardList" size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">Aucun événement d'audit enregistré</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
          <div className="space-y-1">
            {auditLogs?.map((log, idx) => {
              const cfg = getEventConfig(log?.event_type);
              return (
                <div key={log?.id || idx} className="relative flex gap-4 pl-12 pr-2 py-3 hover:bg-muted/20 rounded-lg transition-colors group">
                  <div className={`absolute left-2.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${cfg?.color}`}>
                    <Icon name={cfg?.icon} size={11} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-foreground">{cfg?.label}</p>
                        {log?.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{log?.description}</p>
                        )}
                        {log?.performed_by && (
                          <p className="text-xs text-muted-foreground/70 mt-0.5">Par: {log?.performed_by}</p>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0 whitespace-nowrap">{formatDate(log?.created_at)}</span>
                    </div>
                    {log?.metadata && Object?.keys(log?.metadata)?.length > 0 && (
                      <div className="mt-2 px-3 py-2 bg-muted/30 rounded-lg">
                        {Object?.entries(log?.metadata)?.map(([k, v]) => (
                          <p key={k} className="text-xs text-muted-foreground"><span className="font-medium">{k}:</span> {String(v)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Compliance Status Timeline ───────────────────────────────────────────────
const COMPLIANCE_STEPS = [
  { id: 'registration', label: 'Inscription', description: 'Profil notaire créé dans le système', icon: 'UserPlus' },
  { id: 'document_submission', label: 'Soumission Documents', description: 'Licence et pièces justificatives soumises', icon: 'FileText' },
  { id: 'background_check', label: 'Vérification Antécédents', description: 'Contrôle judiciaire et professionnel', icon: 'UserCheck' },
  { id: 'credential_review', label: 'Revue des Credentials', description: 'Examen des qualifications par l\'équipe', icon: 'Search' },
  { id: 'verification', label: 'Vérification Officielle', description: 'Validation par l\'autorité compétente', icon: 'BadgeCheck' },
  { id: 'active', label: 'Notaire Actif', description: 'Autorisé à opérer sur la plateforme', icon: 'CheckCircle' },
];

const ComplianceStatusTimeline = ({ notary }) => {
  const getStepStatus = (stepId) => {
    if (!notary) return 'pending';
    switch (stepId) {
      case 'registration': return notary?.created_at ? 'completed' : 'pending';
      case 'document_submission': return notary?.license_number ? 'completed' : 'pending';
      case 'background_check': return notary?.background_check_status === 'passed' ? 'completed' : notary?.background_check_status === 'in_progress' ? 'in_progress' : notary?.background_check_status === 'failed' ? 'failed' : 'pending';
      case 'credential_review': return notary?.is_verified ? 'completed' : notary?.license_number ? 'in_progress' : 'pending';
      case 'verification': return notary?.is_verified ? 'completed' : 'pending';
      case 'active': return notary?.is_verified ? 'completed' : 'pending';
      default: return 'pending';
    }
  };

  const getStepDate = (stepId) => {
    switch (stepId) {
      case 'registration': return notary?.created_at;
      case 'verification': case 'active': return notary?.is_verified ? notary?.updated_at : null;
      default: return null;
    }
  };

  const statusConfig = {
    completed: { color: 'bg-green-500 border-green-500', textColor: 'text-green-700', bgColor: 'bg-green-50 border-green-200', label: 'Complété', icon: 'Check' },
    in_progress: { color: 'bg-amber-500 border-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200', label: 'En cours', icon: 'Clock' },
    failed: { color: 'bg-red-500 border-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50 border-red-200', label: 'Échoué', icon: 'X' },
    pending: { color: 'bg-muted border-border', textColor: 'text-muted-foreground', bgColor: 'bg-muted/20 border-border', label: 'En attente', icon: 'Circle' },
  };

  const completedSteps = COMPLIANCE_STEPS?.filter(s => getStepStatus(s?.id) === 'completed')?.length;
  const progressPct = Math?.round((completedSteps / COMPLIANCE_STEPS?.length) * 100);

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-foreground">Timeline de Conformité Réglementaire</h4>
          <span className="text-xs font-semibold text-foreground">{progressPct}% complété</span>
        </div>
        <div className="w-full bg-muted/40 rounded-full h-2">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-amber-500 to-green-500 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">{completedSteps}/{COMPLIANCE_STEPS?.length} étapes complétées</p>
      </div>

      <div className="space-y-3">
        {COMPLIANCE_STEPS?.map((step, idx) => {
          const status = getStepStatus(step?.id);
          const cfg = statusConfig?.[status];
          const date = getStepDate(step?.id);
          const isLast = idx === COMPLIANCE_STEPS?.length - 1;

          return (
            <div key={step?.id} className="relative flex gap-4">
              {!isLast && (
                <div className={`absolute left-4 top-9 w-0.5 h-full -mb-3 ${status === 'completed' ? 'bg-green-300' : 'bg-border'}`} />
              )}
              <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 z-10 ${cfg?.color}`}>
                <Icon name={cfg?.icon} size={14} className="text-white" />
              </div>
              <div className={`flex-1 px-4 py-3 rounded-xl border ${cfg?.bgColor} mb-1`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={`text-sm font-semibold ${cfg?.textColor}`}>{step?.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{step?.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg?.bgColor} ${cfg?.textColor} border`}>
                      {cfg?.label}
                    </span>
                    {date && <span className="text-[10px] text-muted-foreground">{formatDateShort(date)}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Background Verification Status ──────────────────────────────────────────
const BackgroundVerificationStatus = ({ notary }) => {
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!notary?.id) return;
    loadVerificationData();
  }, [notary?.id]);

  const loadVerificationData = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('notary_background_verifications')
        ?.select('*')
        ?.eq('notary_id', notary?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      setVerificationData(data || {
        status: notary?.background_check_status || 'not_started',
        initiated_at: null,
        completed_at: null,
        criminal_record: null,
        professional_standing: null,
        identity_verified: null,
        bar_association_check: null,
        notes: null,
      });
    } catch (err) {
      console.error('Error loading verification data:', err);
      setVerificationData({ status: 'not_started' });
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CONFIG = {
    not_started: { label: 'Non démarré', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: 'Circle', headerBg: 'bg-gray-50 border-gray-200' },
    in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: 'Clock', headerBg: 'bg-amber-50 border-amber-200' },
    passed: { label: 'Validé', color: 'bg-green-100 text-green-700 border-green-200', icon: 'ShieldCheck', headerBg: 'bg-green-50 border-green-200' },
    failed: { label: 'Rejeté', color: 'bg-red-100 text-red-700 border-red-200', icon: 'ShieldX', headerBg: 'bg-red-50 border-red-200' },
    pending_review: { label: 'En revue', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: 'Search', headerBg: 'bg-purple-50 border-purple-200' },
  };

  const CHECK_ITEMS = [
    { key: 'criminal_record', label: 'Casier judiciaire', icon: 'Scale' },
    { key: 'professional_standing', label: 'Statut professionnel', icon: 'Briefcase' },
    { key: 'identity_verified', label: 'Identité vérifiée', icon: 'User' },
    { key: 'bar_association_check', label: 'Barreau / Chambre notariale', icon: 'Building' },
  ];

  const getCheckStatus = (val) => {
    if (val === true || val === 'passed') return { icon: 'CheckCircle', color: 'text-green-600', label: 'Validé' };
    if (val === false || val === 'failed') return { icon: 'XCircle', color: 'text-red-600', label: 'Rejeté' };
    if (val === 'in_progress') return { icon: 'Clock', color: 'text-amber-600', label: 'En cours' };
    return { icon: 'MinusCircle', color: 'text-gray-400', label: 'Non vérifié' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 rounded-full border-2 border-brand-gold border-t-transparent animate-spin" />
      </div>
    );
  }

  const status = verificationData?.status || 'not_started';
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.not_started;

  return (
    <div className="space-y-5">
      {/* Status header */}
      <div className={`rounded-xl border p-5 ${cfg?.headerBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cfg?.color}`}>
              <Icon name={cfg?.icon} size={22} />
            </div>
            <div>
              <h4 className="text-base font-semibold text-foreground">Vérification des Antécédents</h4>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border mt-1 ${cfg?.color}`}>
                <Icon name={cfg?.icon} size={11} />
                {cfg?.label}
              </span>
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground space-y-1">
            {verificationData?.initiated_at && (
              <p>Lancé: {formatDateShort(verificationData?.initiated_at)}</p>
            )}
            {verificationData?.completed_at && (
              <p>Complété: {formatDateShort(verificationData?.completed_at)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Check items grid */}
      <div>
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Contrôles individuels</h5>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CHECK_ITEMS?.map(item => {
            const checkStatus = getCheckStatus(verificationData?.[item?.key]);
            return (
              <div key={item?.key} className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-muted/40 flex items-center justify-center flex-shrink-0">
                  <Icon name={item?.icon} size={16} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{item?.label}</p>
                  <div className={`flex items-center gap-1 mt-0.5 ${checkStatus?.color}`}>
                    <Icon name={checkStatus?.icon} size={12} />
                    <span className="text-xs font-medium">{checkStatus?.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Notes */}
      {verificationData?.notes && (
        <div className="p-4 bg-muted/30 border border-border rounded-xl">
          <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Notes de vérification</h5>
          <p className="text-sm text-foreground">{verificationData?.notes}</p>
        </div>
      )}

      {/* Action buttons */}
      {status === 'not_started' && (
        <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-sm font-medium transition-all">
          <Icon name="UserCheck" size={16} />
          Lancer la vérification des antécédents
        </button>
      )}
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const NotaryRegulatoryPanel = ({ notary }) => {
  const [activeTab, setActiveTab] = useState('audit');

  const tabs = [
    { id: 'audit', label: 'Audit Credentials', icon: 'ClipboardList' },
    { id: 'compliance', label: 'Timeline Conformité', icon: 'GitBranch' },
    { id: 'background', label: 'Vérification Antécédents', icon: 'UserCheck' },
  ];

  return (
    <div className="space-y-5">
      {/* Tab navigation */}
      <div className="flex gap-1 p-1 bg-muted/30 rounded-xl border border-border">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab?.id
                ? 'bg-card text-foreground shadow-sm border border-border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={13} />
            <span className="hidden sm:inline">{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="min-h-[300px]">
        {activeTab === 'audit' && <CredentialAuditTrail notary={notary} />}
        {activeTab === 'compliance' && <ComplianceStatusTimeline notary={notary} />}
        {activeTab === 'background' && <BackgroundVerificationStatus notary={notary} />}
      </div>
    </div>
  );
};

export default NotaryRegulatoryPanel;
